import { Link } from 'react-router-dom';
import { Crown, Check, X, Zap, Users, Sparkles } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuthStore } from '@/stores/auth';
import clsx from 'clsx';

export default function PricingPage() {
    const { t } = useTranslation();
    usePageTitle('pricing.title');
    const { user } = useAuthStore();
    const currentPlan = (user as any)?.plan || 'free';

    const features = [
        {
            name: t('pricing.features.workspaces', 'Workspaces'),
            free: '1',
            pro: t('pricing.unlimited', 'Không giới hạn'),
        },
        {
            name: t('pricing.features.members', 'Thành viên / workspace'),
            free: '5',
            pro: t('pricing.unlimited', 'Không giới hạn'),
        },
        {
            name: t('pricing.features.boards', 'Boards'),
            free: '3',
            pro: t('pricing.unlimited', 'Không giới hạn'),
        },
        {
            name: t('pricing.features.objectives', 'Objectives (OKRs)'),
            free: '5',
            pro: t('pricing.unlimited', 'Không giới hạn'),
        },
        {
            name: t('pricing.features.tasks', 'Tasks'),
            free: '50',
            pro: t('pricing.unlimited', 'Không giới hạn'),
        },
        {
            name: t('pricing.features.analytics', 'Analytics & Reports'),
            free: t('pricing.basic', 'Cơ bản'),
            pro: t('pricing.advanced', 'Nâng cao'),
        },
        {
            name: t('pricing.features.teamAnalytics', 'Team Analytics'),
            free: false,
            pro: true,
        },
        {
            name: t('pricing.features.prioritySupport', 'Hỗ trợ ưu tiên'),
            free: false,
            pro: true,
        },
        {
            name: t('pricing.features.customBranding', 'Custom branding'),
            free: false,
            pro: true,
        },
        {
            name: t('pricing.features.apiAccess', 'API Access'),
            free: false,
            pro: true,
        },
    ];

    return (
        <div className="p-6 max-w-5xl mx-auto">
            <div className="text-center mb-10">
                <h1 className="text-3xl font-bold text-neutral-800 dark:text-neutral-100 mb-2">
                    {t('pricing.title', 'Chọn gói phù hợp')}
                </h1>
                <p className="text-neutral-500 dark:text-neutral-400">
                    {t('pricing.subtitle', 'Nâng cấp để mở khóa tất cả tính năng')}
                </p>
            </div>

            {/* Pricing Cards */}
            <div className="grid md:grid-cols-2 gap-6 mb-10">
                {/* FREE Plan */}
                <div className={clsx(
                    'bg-white dark:bg-neutral-800 rounded-2xl border-2 p-6',
                    currentPlan === 'free' ? 'border-primary-500' : 'border-neutral-200 dark:border-neutral-700'
                )}>
                    {currentPlan === 'free' && (
                        <div className="inline-block px-3 py-1 bg-primary-100 dark:bg-primary-600/20 text-primary-600 text-xs font-medium rounded-full mb-4">
                            {t('pricing.currentPlan', 'Gói hiện tại')}
                        </div>
                    )}
                    <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-6 h-6 text-primary-500" />
                        <h2 className="text-xl font-bold">FREE</h2>
                    </div>
                    <p className="text-neutral-500 dark:text-neutral-400 mb-4">
                        {t('pricing.freeDesc', 'Dành cho cá nhân và dự án nhỏ')}
                    </p>
                    <div className="mb-6">
                        <span className="text-4xl font-bold text-neutral-800 dark:text-neutral-100">0đ</span>
                        <span className="text-neutral-500"> / {t('pricing.forever', 'mãi mãi')}</span>
                    </div>
                    <ul className="space-y-3 mb-6">
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>1 workspace</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>5 thành viên</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>3 boards, 5 objectives</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Basic analytics</span>
                        </li>
                    </ul>
                    {currentPlan === 'free' && (
                        <button disabled className="w-full btn-secondary opacity-50 cursor-not-allowed">
                            {t('pricing.currentPlan', 'Gói hiện tại')}
                        </button>
                    )}
                </div>

                {/* PRO Plan */}
                <div className={clsx(
                    'rounded-2xl border-2 p-6 relative overflow-hidden',
                    currentPlan === 'pro'
                        ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-500'
                        : 'bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-amber-400'
                )}>
                    <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-amber-200/50 to-orange-200/50 dark:from-amber-600/20 dark:to-orange-600/20 rounded-full -translate-y-16 translate-x-16" />

                    {currentPlan === 'pro' && (
                        <div className="inline-block px-3 py-1 bg-amber-500 text-white text-xs font-medium rounded-full mb-4">
                            {t('pricing.currentPlan', 'Gói hiện tại')}
                        </div>
                    )}
                    <div className="flex items-center gap-2 mb-2 relative z-10">
                        <Crown className="w-6 h-6 text-amber-500" />
                        <h2 className="text-xl font-bold">PRO</h2>
                        <Sparkles className="w-4 h-4 text-amber-400" />
                    </div>
                    <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                        {t('pricing.proDesc', 'Dành cho team và doanh nghiệp')}
                    </p>
                    <div className="mb-6">
                        <span className="text-4xl font-bold text-neutral-800 dark:text-neutral-100">99.000đ</span>
                        <span className="text-neutral-500"> / {t('pricing.month', 'tháng')}</span>
                    </div>
                    <ul className="space-y-3 mb-6">
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>{t('pricing.unlimited', 'Không giới hạn')} workspaces</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>{t('pricing.unlimited', 'Không giới hạn')} thành viên</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>{t('pricing.unlimited', 'Không giới hạn')} boards & tasks</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Team Analytics & Reports</span>
                        </li>
                        <li className="flex items-center gap-2 text-sm">
                            <Check className="w-4 h-4 text-green-500" />
                            <span>Priority support & API access</span>
                        </li>
                    </ul>
                    {currentPlan === 'pro' ? (
                        <button disabled className="w-full bg-amber-500 text-white py-2 px-4 rounded-lg opacity-50 cursor-not-allowed">
                            {t('pricing.currentPlan', 'Gói hiện tại')}
                        </button>
                    ) : (
                        <button className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-2.5 px-4 rounded-lg font-medium transition-all shadow-lg shadow-amber-500/25">
                            {t('pricing.upgrade', 'Nâng cấp PRO')}
                        </button>
                    )}
                </div>
            </div>

            {/* Feature Comparison Table */}
            <div className="bg-white dark:bg-neutral-800 rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden">
                <div className="p-6 border-b border-neutral-200 dark:border-neutral-700">
                    <h2 className="text-xl font-bold text-neutral-800 dark:text-neutral-100">
                        {t('pricing.compareTitle', 'So sánh chi tiết')}
                    </h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-neutral-200 dark:border-neutral-700">
                                <th className="text-left p-4 font-medium text-neutral-600 dark:text-neutral-400">
                                    {t('pricing.feature', 'Tính năng')}
                                </th>
                                <th className="p-4 font-medium text-neutral-600 dark:text-neutral-400 text-center w-32">
                                    <div className="flex items-center justify-center gap-1">
                                        <Zap className="w-4 h-4 text-primary-500" />
                                        FREE
                                    </div>
                                </th>
                                <th className="p-4 font-medium text-neutral-600 dark:text-neutral-400 text-center w-32">
                                    <div className="flex items-center justify-center gap-1">
                                        <Crown className="w-4 h-4 text-amber-500" />
                                        PRO
                                    </div>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {features.map((feature, i) => (
                                <tr key={i} className="border-b border-neutral-100 dark:border-neutral-700 last:border-0">
                                    <td className="p-4 text-sm text-neutral-700 dark:text-neutral-300">
                                        {feature.name}
                                    </td>
                                    <td className="p-4 text-center">
                                        {typeof feature.free === 'boolean' ? (
                                            feature.free ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                            ) : (
                                                <X className="w-5 h-5 text-neutral-300 mx-auto" />
                                            )
                                        ) : (
                                            <span className="text-sm text-neutral-600 dark:text-neutral-400">{feature.free}</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-center">
                                        {typeof feature.pro === 'boolean' ? (
                                            feature.pro ? (
                                                <Check className="w-5 h-5 text-green-500 mx-auto" />
                                            ) : (
                                                <X className="w-5 h-5 text-neutral-300 mx-auto" />
                                            )
                                        ) : (
                                            <span className="text-sm font-medium text-amber-600">{feature.pro}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Free PRO via Referral */}
            <div className="mt-6 bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-2xl p-6 text-center">
                <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-100 mb-2">
                    {t('pricing.freeProTitle', '🎁 Nhận PRO miễn phí!')}
                </h3>
                <p className="text-neutral-600 dark:text-neutral-400 mb-4">
                    {t('pricing.freeProDesc', 'Mời 5 bạn bè đăng ký để nhận 1 tháng PRO miễn phí')}
                </p>
                <Link
                    to="/referral"
                    className="inline-flex items-center gap-2 btn-primary"
                >
                    <Users className="w-4 h-4" />
                    {t('pricing.inviteFriends', 'Mời bạn bè ngay')}
                </Link>
            </div>
        </div>
    );
}
